import React, {PureComponent} from "react"
import {isNull, isNaN} from "lodash"

import {Product} from "@irati/types"
import id from "@irati/utils/id"
import {InjectIntl, injectIntl} from "@irati/utils/lang"
import Validation from "@irati/utils/validation"
import {twoDecimalPlaces} from "@irati/utils/validation/regex/number"
import {getNormalisedValue} from "@irati/utils/currency/formatCurrency"

import basket from "@irati/checkout/utils/basket"
import {StyledInput} from "./styled/OpenValueInput"

interface OptionalProps {
  name?: string
  placeholder?: string
  label?: string
}

interface Props extends InjectIntl, OptionalProps {
  product: Product
  minValue: number
  maxValue: number
  handleChange: (uuid: string, value: number) => void
}

const MAX_INPUT_LENGTH = 8

class OpenValueInput extends PureComponent<Props> {
  static defaultProps: OptionalProps = {
    name: "openValueInput",
    placeholder: "perks.productForms.openValueProduct.input.placeholder",
    label: ""
  }

  validationSchema = () => {
    const {minValue, maxValue, formatMessage} = this.props

    return Validation.string(formatMessage({id: "perks.productForms.openValueProduct.required.validation"}))
      .matches(twoDecimalPlaces, formatMessage({id: "perks.productForms.openValueProduct.twoDecimalPlaces.validation"}))
      .test(
        "positive",
        formatMessage({id: "perks.productForms.openValueProduct.positiveNumber.validation"}),
        (value: string) => parseFloat(value) > 0 || isNull(value)
      )
      .test(
        "less-than-or-equal-to-max",
        formatMessage({id: "perks.productForms.openValueProduct.maxValue.validation"}),
        (value: string) => {
          const currentValue = parseFloat(value)
          return !currentValue || currentValue <= maxValue || !maxValue
        }
      )
      .test(
        "greater-than-or-equal-to-min",
        formatMessage({id: "perks.productForms.openValueProduct.minValue.validation"}),
        (value: string) => {
          const currentValue = parseFloat(value)
          return !currentValue || currentValue >= minValue || !minValue
        }
      )
  }

  getValueFromBasket = (productUuid: string) => {
    const openValueInCents = basket.items.find((item) => item.productId === productUuid)?.openValue
    const currency = basket.breakdown.total.currency
    return openValueInCents ? getNormalisedValue(true, currency, openValueInCents) : null
  }

  maxLengthCheck(e: React.KeyboardEvent<HTMLInputElement>) {
    const {product} = this.props

    const openValue = this.getValueFromBasket(product.uuid)

    if (openValue && openValue.toString().length > MAX_INPUT_LENGTH - 1) {
      if (!isNaN(Number(e.key))) {
        e.currentTarget.value = e.currentTarget.value.slice(0, MAX_INPUT_LENGTH - 1)
      }
    }
  } // Restrict users from entering more characters than the MAX_INPUT_LENGTH

  render() {
    const {handleChange, product, label, name, placeholder, maxValue} = this.props

    const openValue = this.getValueFromBasket(product.uuid)

    return (
      <StyledInput
        required
        type="number"
        min={0}
        max={maxValue}
        maxLength={MAX_INPUT_LENGTH}
        includeCharCountdown={false}
        label={label}
        name={name}
        placeholder={placeholder}
        value={openValue}
        onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => this.maxLengthCheck(e)}
        onChange={(value: number) => handleChange(product.uuid, value)}
        validation={this.validationSchema()}
        onPaste={(e: React.KeyboardEvent<HTMLInputElement>) => e.preventDefault()}
        id={id("openValueInput", this.props)}
        onEnter={(e: React.KeyboardEvent<HTMLInputElement>) => e.preventDefault()}
      />
    )
  }
}

export default injectIntl(OpenValueInput)
